import { Language } from '../hooks/useLanguage';

export interface ModelInfo {
  id: string;
  name: string;
  provider: string;
  pricing?: {
    prompt: number;
    completion: number;
  };
  contextLength?: number;
  description?: string;
  category: 'popular' | 'premium' | 'economic' | 'specialized';
  isRecommended?: boolean;
}

// Cache for API responses
let modelsCache: ModelInfo[] | null = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Fallback models with popularity ranking based on community usage and performance
const FALLBACK_MODELS: ModelInfo[] = [
  // Most Popular / Recommended
  { 
    id: 'anthropic/claude-3.5-sonnet', 
    name: 'Claude 3.5 Sonnet', 
    provider: 'Anthropic',
    category: 'popular',
    isRecommended: true,
    description: 'Balanced performance and speed'
  },
  { 
    id: 'openai/gpt-4-turbo', 
    name: 'GPT-4 Turbo', 
    provider: 'OpenAI',
    category: 'popular',
    isRecommended: true,
    description: 'Latest GPT-4 with improved performance'
  },
  { 
    id: 'openai/gpt-3.5-turbo', 
    name: 'GPT-3.5 Turbo', 
    provider: 'OpenAI',
    category: 'economic',
    isRecommended: false,
    description: 'Fast and cost-effective'
  },

  // Premium Models
  { 
    id: 'anthropic/claude-3-opus', 
    name: 'Claude 3 Opus', 
    provider: 'Anthropic',
    category: 'premium',
    isRecommended: false,
    description: 'Highest capability model'
  },
  { 
    id: 'openai/gpt-4', 
    name: 'GPT-4', 
    provider: 'OpenAI',
    category: 'premium',
    isRecommended: false,
    description: 'Most capable OpenAI model'
  },

  // Economic Options
  { 
    id: 'anthropic/claude-3-haiku', 
    name: 'Claude 3 Haiku', 
    provider: 'Anthropic',
    category: 'economic',
    isRecommended: false,
    description: 'Fast and affordable'
  },
  { 
    id: 'meta-llama/llama-3.1-8b-instruct', 
    name: 'Llama 3.1 8B', 
    provider: 'Meta',
    category: 'economic',
    isRecommended: false,
    description: 'Open source alternative'
  },

  // Specialized Models
  { 
    id: 'meta-llama/llama-3.1-70b-instruct', 
    name: 'Llama 3.1 70B', 
    provider: 'Meta',
    category: 'specialized',
    isRecommended: false,
    description: 'Large open source model'
  },
  { 
    id: 'mistralai/mistral-7b-instruct', 
    name: 'Mistral 7B', 
    provider: 'Mistral AI',
    category: 'economic',
    isRecommended: false,
    description: 'European AI alternative'
  },
  { 
    id: 'google/gemini-pro-1.5', 
    name: 'Gemini Pro 1.5', 
    provider: 'Google',
    category: 'popular',
    isRecommended: false,
    description: 'Google\'s latest multimodal model'
  }
];

export const fetchAvailableModels = async (): Promise<ModelInfo[]> => {
  // Check cache first
  const now = Date.now();
  if (modelsCache && (now - cacheTimestamp) < CACHE_DURATION) {
    return modelsCache;
  }

  try {
    const response = await fetch('https://openrouter.ai/api/v1/models', {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    
    if (data.data && Array.isArray(data.data)) {
      const models: ModelInfo[] = data.data
        .map((model: any) => {
          // Determine category based on model characteristics
          let category: ModelInfo['category'] = 'specialized';
          let isRecommended = false;

          // Popular models (based on known usage patterns)
          if (
            model.id.includes('claude-3.5-sonnet') ||
            model.id.includes('gpt-4-turbo') ||
            model.id.includes('gemini-pro')
          ) {
            category = 'popular';
            isRecommended = true;
          }
          // Premium models (high-end capabilities)
          else if (
            model.id.includes('claude-3-opus') ||
            model.id.includes('gpt-4') && !model.id.includes('turbo') ||
            model.id.includes('claude-4')
          ) {
            category = 'premium';
          }
          // Economic models (cost-effective)
          else if (
            model.id.includes('gpt-3.5') ||
            model.id.includes('claude-3-haiku') ||
            model.id.includes('llama') ||
            model.id.includes('mistral-7b')
          ) {
            category = 'economic';
          }

          return {
            id: model.id,
            name: model.name || model.id.split('/').pop() || model.id,
            provider: model.id.split('/')[0] || 'Unknown',
            pricing: model.pricing ? {
              prompt: parseFloat(model.pricing.prompt) || 0,
              completion: parseFloat(model.pricing.completion) || 0
            } : undefined,
            contextLength: model.context_length || undefined,
            description: model.description || '',
            category,
            isRecommended
          };
        })
        // Sort by category priority and recommendation
        .sort((a: ModelInfo, b: ModelInfo) => {
          // Recommended models first
          if (a.isRecommended && !b.isRecommended) return -1;
          if (!a.isRecommended && b.isRecommended) return 1;
          
          // Then by category priority
          const categoryOrder: Record<ModelInfo['category'], number> = { 
            popular: 0, 
            premium: 1, 
            economic: 2, 
            specialized: 3 
          };
          const categoryDiff = categoryOrder[a.category] - categoryOrder[b.category];
          if (categoryDiff !== 0) return categoryDiff;
          
          // Finally by name
          return a.name.localeCompare(b.name);
        });

      // Cache the result
      modelsCache = models;
      cacheTimestamp = now;
      
      return models;
    }
  } catch (error) {
    console.warn('Failed to fetch models from OpenRouter API, using fallback:', error);
  }

  // Return fallback models if API fails
  return FALLBACK_MODELS;
};

export const getModelCategories = (language: Language) => {
  const categories = {
    'pt-BR': {
      popular: 'Mais Populares',
      premium: 'Premium',
      economic: 'Econômicos', 
      specialized: 'Especializados'
    },
    'en': {
      popular: 'Most Popular',
      premium: 'Premium',
      economic: 'Economic',
      specialized: 'Specialized'
    }
  };

  return categories[language];
};

export const getRecommendedModels = async (): Promise<ModelInfo[]> => {
  const models = await fetchAvailableModels();
  return models.filter(model => model.isRecommended);
};

// Get recommended models by category/capability
export const getRecommendedModelsByCategory = async (category: 'vision' | 'text' | 'code' | 'creative' | 'reasoning'): Promise<ModelInfo[]> => {
  const models = await fetchAvailableModels();
  
  // Define model preferences by category
  const categoryPreferences = {
    vision: [
      'anthropic/claude-3.5-sonnet',
      'openai/gpt-4-vision-preview', 
      'openai/gpt-4o',
      'google/gemini-1.5-pro',
      'google/gemini-1.5-flash'
    ],
    text: [
      'anthropic/claude-3.5-sonnet',
      'openai/gpt-4-turbo',
      'openai/gpt-4',
      'google/gemini-1.5-pro',
      'meta-llama/llama-3.1-405b-instruct'
    ],
    code: [
      'anthropic/claude-3.5-sonnet',
      'openai/gpt-4',
      'deepseek/deepseek-coder',
      'codellama/codellama-70b-instruct',
      'meta-llama/llama-3.1-70b-instruct'
    ],
    creative: [
      'anthropic/claude-3.5-sonnet',
      'openai/gpt-4',
      'google/gemini-1.5-pro',
      'meta-llama/llama-3.1-405b-instruct',
      'mistralai/mistral-large'
    ],
    reasoning: [
      'anthropic/claude-3.5-sonnet',
      'openai/o1-preview',
      'openai/o1-mini',
      'google/gemini-1.5-pro',
      'meta-llama/llama-3.1-405b-instruct'
    ]
  };

  const preferredIds = categoryPreferences[category];
  const availableModels: ModelInfo[] = [];

  // Find available models in order of preference
  for (const modelId of preferredIds) {
    const model = models.find(m => m.id === modelId);
    if (model) {
      availableModels.push(model);
      if (availableModels.length >= 5) break; // Limit to 5 models per category
    }
  }

  // If we don't have enough models, add popular ones
  if (availableModels.length < 3) {
    const popularModels = models
      .filter(m => m.category === 'popular' && !availableModels.some(am => am.id === m.id))
      .slice(0, 3 - availableModels.length);
    availableModels.push(...popularModels);
  }

  return availableModels;
};